import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { CATEGORY_COLORS } from "@/lib/charts";

const StatCard = ({ label, value, testId, tone }) => (
  <div data-testid={testId} className="rounded-[10px] border border-line bg-white p-3">
    <p className="text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">{label}</p>
    <p className="mt-1 text-[22px] font-semibold tabular-nums" style={{ color: tone || "var(--ink)" }}>{value}</p>
  </div>
);

const pctTone = (v, n) => {
  if (v == null || (n ?? 0) < 10) return "var(--ink-tertiary)";
  if (v >= 80) return "var(--state-ok)";
  if (v >= 60) return "var(--state-warn)";
  return "var(--state-critical)";
};
const MIN_MUESTRA = 10;
const pctLabel = (t, v, n) => {
  if (v == null) return "—";
  if ((n ?? 0) < MIN_MUESTRA) return t("intake.precision.insufficient_sample", { n: n ?? 0 });
  return `${v}% · ${t("intake.precision.n_cases", { n })}`;
};

export const IntakePrecisionTab = () => {
  const { t } = useT();
  const { data, isLoading } = useQuery({
    queryKey: ["intake-precision"],
    queryFn: async () => (await api.get("/intake/precision")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="grid gap-3 p-5 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[80px] rounded-[10px]" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5 overflow-y-auto p-5" data-testid="intake-precision-tab">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard testId="intake-precision-borradores" label={t("intake.precision.drafts")} value={data.borradores_creados} />
        <StatCard testId="intake-precision-confirmados" label={t("intake.precision.confirmed")} value={data.confirmados} tone="var(--state-ok)" />
        <StatCard testId="intake-precision-descartados" label={t("intake.precision.discarded")} value={data.descartados} tone="var(--ink-tertiary)" />
        <StatCard testId="intake-precision-pendientes" label={t("intake.precision.pending")} value={data.pendientes} tone="var(--state-warn)" />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard testId="intake-precision-edificio-remitente" label={t("intake.precision.building_sender")}
          value={pctLabel(t, data.precision_edificio_remitente_pct, data.precision_edificio_remitente_n)}
          tone={pctTone(data.precision_edificio_remitente_pct, data.precision_edificio_remitente_n)} />
        <StatCard testId="intake-precision-edificio-modelo" label={t("intake.precision.building_model")}
          value={pctLabel(t, data.precision_edificio_modelo_pct, data.precision_edificio_modelo_n)}
          tone={pctTone(data.precision_edificio_modelo_pct, data.precision_edificio_modelo_n)} />
        <StatCard testId="intake-precision-unidad" label={t("intake.precision.unit")}
          value={pctLabel(t, data.precision_unidad_pct, data.precision_unidad_n)}
          tone={pctTone(data.precision_unidad_pct, data.precision_unidad_n)} />
        <StatCard testId="intake-precision-descripcion" label={t("intake.precision.description")}
          value={pctLabel(t, data.precision_descripcion_pct, data.precision_descripcion_n)}
          tone={pctTone(data.precision_descripcion_pct, data.precision_descripcion_n)} />
        <StatCard testId="intake-precision-servicio" label={t("intake.precision.service")}
          value={pctLabel(t, data.precision_servicio_pct, data.precision_servicio_n)}
          tone={pctTone(data.precision_servicio_pct, data.precision_servicio_n)} />
        <StatCard testId="intake-precision-prioridad" label={t("intake.precision.priority")}
          value={pctLabel(t, data.precision_prioridad_pct, data.precision_prioridad_n)}
          tone={pctTone(data.precision_prioridad_pct, data.precision_prioridad_n)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard testId="intake-precision-tiempo" label={t("intake.precision.avg_time")}
          value={data.tiempo_promedio_confirmacion_min != null ? `${data.tiempo_promedio_confirmacion_min} min` : "—"} />
        <StatCard testId="intake-precision-costo" label={t("intake.precision.cost")}
          value={formatMoney(Math.round(data.costo_acumulado_cents || 0))} />
      </div>

      <div className="rounded-[10px] border border-line bg-white p-3">
        <p className="mb-2 text-[12.5px] font-semibold text-ink">{t("intake.precision.weekly_evolution")}</p>
        {(data.evolucion_semanal?.length ?? 0) >= 3 ? (
          <ResponsiveContainer width="100%" height={220} data-testid="intake-precision-chart">
            <LineChart data={data.evolucion_semanal}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="semana" tick={{ fontSize: 11, fill: "var(--ink-tertiary)" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--ink-tertiary)" }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Line isAnimationActive={false} type="monotone" dataKey="precision_pct" stroke={CATEGORY_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p data-testid="intake-precision-chart-empty" className="py-6 text-center text-[12.5px] text-ink-tertiary">
            {t("intake.precision.not_enough_weeks")}
          </p>
        )}
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        <p className="border-b border-line bg-surface-sunken px-3 py-2 text-[12.5px] font-semibold text-ink">
          {t("intake.precision.failed_cases")}
        </p>
        {!data.casos_fallidos?.length ? (
          <p className="p-4 text-center text-[12.5px] text-ink-tertiary">{t("intake.precision.no_failed")}</p>
        ) : (
          <table className="w-full text-[12px]">
            <tbody>
              {data.casos_fallidos.map((c, i) => (
                <tr key={i} data-testid={`intake-failed-case-${i}`} className="border-b border-line last:border-0">
                  <td className="w-[100px] px-3 py-2 font-medium text-ink">{c.wo_code}</td>
                  <td className="px-3 py-2 text-ink-tertiary">{c.mensaje_extracto}</td>
                  <td className="px-3 py-2 text-right text-state-critical">{c.campos_corregidos.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
